import React, { useEffect, useRef, useState } from 'react'

import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Stack from 'react-bootstrap/Stack'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import Button from 'react-bootstrap/Button'

import { InfoIcon } from '../../../InfoIcon'
import { UploadImage } from '../../../UploadImage'
import { TrashButton } from '../../../TrashButton'
import { searchAttractions } from '../../../../utilities/api'
import AuthService from '../../../../utilities/services/auth.service'

import clearIcon from '../../../../assets/icons/close-circled.svg'
import searchIcon from '../../../../assets/icons/search-icon.svg'

const SEARCH_DEBOUNCE_MS = 350

function isReusedRemoteArtwork(artwork) {
  return Boolean(
    artwork?.id && artwork?.url && String(artwork.url).startsWith('https://')
  )
}

export default function AddAttractionModal({
  show,
  handleClose,
  id,
  attraction,
  setAttraction,
  image,
  setImage,
  coordinates,
  setCoordinates,
  handleSubmit,
  isDisabled,
}) {
  const organizationUUID = AuthService.getOrg()?.[0]?.uuid
  const inputRef = useRef(null)
  const searchRequestId = useRef(0)
  const suppressMenuRef = useRef(false)

  const [name, setName] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [menuOpen, setMenuOpen] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  // Name that currently owns the auto-filled artwork from search
  const [matchedName, setMatchedName] = useState(null)

  useEffect(() => {
    if (!show) return

    const initialName = attraction?.name || ''
    setName(initialName)
    setMatchedName(
      isReusedRemoteArtwork(attraction?.artwork) ? initialName : null
    )
    setSuggestions([])
    setMenuOpen(false)
    setIsSearching(false)
    setActiveIndex(-1)
    suppressMenuRef.current = false

    const focusTimer = setTimeout(() => inputRef.current?.focus(), 0)
    return () => clearTimeout(focusTimer)
  }, [show, id])

  // Debounced search — never mutates the input value
  useEffect(() => {
    if (!show || !organizationUUID) return

    const query = name.trim()
    if (!query) {
      setSuggestions([])
      setMenuOpen(false)
      setIsSearching(false)
      setActiveIndex(-1)
      return
    }

    const requestId = ++searchRequestId.current
    setIsSearching(true)

    const timer = setTimeout(async () => {
      try {
        const res = await searchAttractions(organizationUUID, query)
        if (requestId !== searchRequestId.current) return

        setSuggestions(res?.data || [])
        if (!suppressMenuRef.current) {
          setMenuOpen(true)
        }
        setActiveIndex(-1)
      } catch (err) {
        if (requestId !== searchRequestId.current) return
        console.error(err)
        setSuggestions([])
        if (!suppressMenuRef.current) {
          setMenuOpen(true)
        }
      } finally {
        if (requestId === searchRequestId.current) {
          setIsSearching(false)
        }
      }
    }, SEARCH_DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [name, show, organizationUUID])

  const dropReusedArtworkIfNameChanged = (typed) => {
    if (!matchedName || typed === matchedName) return

    setMatchedName(null)
    setAttraction((prev) => {
      if (!isReusedRemoteArtwork(prev?.artwork)) return { ...prev, name: typed }
      return {
        ...prev,
        name: typed,
        artwork: undefined,
      }
    })
    setImage()
    setCoordinates()
  }

  const handleNameChange = (e) => {
    const nextName = e.target.value
    suppressMenuRef.current = false
    setName(nextName)
    setAttraction((prev) => ({
      ...prev,
      name: nextName,
    }))
  }

  const handleClear = () => {
    searchRequestId.current += 1
    suppressMenuRef.current = false
    setName('')
    setMatchedName(null)
    setSuggestions([])
    setMenuOpen(false)
    setIsSearching(false)
    setActiveIndex(-1)
    setAttraction((prev) => ({
      ...prev,
      name: '',
      artwork: undefined,
    }))
    setImage()
    setCoordinates()
    inputRef.current?.focus()
  }

  const applyMatch = (match) => {
    if (!match) return

    searchRequestId.current += 1
    suppressMenuRef.current = true
    setName(match.name)
    setMatchedName(match.name)
    setSuggestions([])
    setMenuOpen(false)
    setIsSearching(false)
    setActiveIndex(-1)

    const artwork = match.artwork
    setAttraction((prev) => ({
      ...prev,
      name: match.name,
      artwork: artwork
        ? {
            id: artwork.id,
            url: artwork.url,
            width: artwork.width,
            height: artwork.height,
          }
        : undefined,
    }))

    if (artwork?.url) {
      setImage(artwork.url)
      setCoordinates({
        width: artwork.width || 1080,
        height: artwork.height || 1080,
      })
    } else {
      setImage()
      setCoordinates()
    }
  }

  const handleRemove = () => {
    setMatchedName(null)
    setImage()
    setCoordinates()
    setAttraction((prev) => ({
      ...prev,
      artwork: prev?.artwork
        ? { ...prev.artwork, id: undefined, url: undefined }
        : undefined,
    }))
  }

  const handleSetImage = (nextImage) => {
    setImage(nextImage)
    if (nextImage && !String(nextImage).startsWith('https://')) {
      setMatchedName(null)
      setAttraction((prev) => ({
        ...prev,
        artwork: {
          ...(prev?.artwork || {}),
          id: undefined,
          url: nextImage,
        },
      }))
    }
  }

  const commitTypedName = () => {
    const typed = name.trim()
    if (!typed) {
      setAttraction((prev) => ({ ...prev, name: '' }))
      return
    }

    if (matchedName && typed !== matchedName) {
      dropReusedArtworkIfNameChanged(typed)
      return
    }

    setAttraction((prev) =>
      typed !== prev?.name ? { ...prev, name: typed } : prev
    )
  }

  const handleKeyDown = (e) => {
    if (!menuOpen) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) =>
        suggestions.length ? Math.min(i + 1, suggestions.length - 1) : -1
      )
      return
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, -1))
      return
    }

    if (e.key === 'Enter' && activeIndex >= 0 && suggestions[activeIndex]) {
      e.preventDefault()
      applyMatch(suggestions[activeIndex])
      return
    }

    if (e.key === 'Escape') {
      setMenuOpen(false)
      setActiveIndex(-1)
    }
  }

  const showMenu = menuOpen && name.trim().length > 0

  return (
    <Modal
      show={show}
      animation={false}
      onHide={handleClose}
      centered
      backdrop="static"
      className="modal-lg"
    >
      <Modal.Body>
        <div className="modal-body-heading">
          <h1 className="modal-body-heading-title">
            {id ? 'Edit' : 'Add'} attraction
          </h1>
        </div>
        <Form
          onSubmit={(e) => {
            commitTypedName()
            handleSubmit(e)
          }}
        >
          <Form.Group className="form-group" controlId="name">
            <Form.Label>Name</Form.Label>
            <div className="attraction-name-field">
              <div className="attraction-name-field__control">
                <img
                  src={searchIcon}
                  alt=""
                  className="attraction-name-field__search-icon"
                />
                <Form.Control
                  ref={inputRef}
                  type="text"
                  name="attractionName"
                  autoComplete="off"
                  placeholder="Search or enter attraction name"
                  value={name}
                  onChange={handleNameChange}
                  onKeyDown={handleKeyDown}
                  onBlur={() => {
                    // Delay so suggestion click can fire before the menu closes
                    setTimeout(() => {
                      setMenuOpen(false)
                      setActiveIndex(-1)
                      commitTypedName()
                    }, 150)
                  }}
                  onFocus={() => {
                    if (name.trim()) setMenuOpen(true)
                  }}
                  required
                />
                {name ? (
                  <button
                    type="button"
                    className="attraction-name-field__clear"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleClear}
                    aria-label="Clear name"
                  >
                    <img src={clearIcon} alt="" />
                  </button>
                ) : null}
              </div>

              {showMenu ? (
                <ul
                  className="attraction-name-field__menu"
                  role="listbox"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {isSearching && suggestions.length === 0 ? (
                    <li className="attraction-name-field__status">
                      Searching…
                    </li>
                  ) : null}
                  {!isSearching && suggestions.length === 0 ? (
                    <li className="attraction-name-field__status">
                      No matching attractions — keep typing to use this name
                    </li>
                  ) : null}
                  {suggestions.map((match, index) => (
                    <li
                      key={match.id || `${match.name}-${index}`}
                      role="option"
                      aria-selected={index === activeIndex}
                      className={`attraction-name-field__option${
                        index === activeIndex
                          ? ' attraction-name-field__option--active'
                          : ''
                      }`}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => applyMatch(match)}
                    >
                      {match.artwork?.url ? (
                        <img
                          src={match.artwork.url}
                          alt=""
                          width={36}
                          height={36}
                          className="attraction-name-field__thumb"
                        />
                      ) : (
                        <span className="attraction-name-field__thumb attraction-name-field__thumb--empty" />
                      )}
                      <span>{match.name}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </Form.Group>
          <Form.Group className="form-group" controlId="thumbnail">
            <div className="form-label--flex">
              <Form.Label>Event thumbnail</Form.Label>
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip>
                    Use a high quality image in 1:1 ratio (e.g 1080 x 1080px).
                    Selecting a past attraction will fill this automatically.
                  </Tooltip>
                }
              >
                <Button variant="link">
                  <InfoIcon />
                </Button>
              </OverlayTrigger>
            </div>
            <div className={`${image ? 'flex' : ''} `}>
              <UploadImage
                selectedImage={image}
                setSelectedImage={handleSetImage}
                coordinates={coordinates}
                setCoordinates={setCoordinates}
                altText={`Portrait of ${attraction?.name || name || ''} `}
              />
              {image && <TrashButton onClick={handleRemove} />}
            </div>
          </Form.Group>
          <Stack direction="horizontal" className="btn-group-flex">
            <Button variant="outline-light" size="lg" onClick={handleClose}>
              Cancel
            </Button>
            <Button disabled={isDisabled} type="submit" size="lg">
              Apply
            </Button>
          </Stack>
        </Form>
      </Modal.Body>
    </Modal>
  )
}
